import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { PickerItemRegistration } from "./PickerContext";
import { summarizeSelection } from "./PickerSelectionSummary";
import { collectItems } from "./PickerTree";
import { useLatestCallback, useShallowStable } from "./usePickerStable";
import type {
    PickerItemData,
    PickerItemValue,
    PickerSelectionSummary,
} from "./Picker.types";
import { keepOnePerExclusiveSection } from "./pickerExclusiveSections";

type LooseValue<T extends PickerItemValue> =
    | readonly T[]
    | T
    | null
    | undefined;

const isValueList = <T extends PickerItemValue>(
    value: NonNullable<LooseValue<T>>,
): value is readonly T[] => Array.isArray(value);

/** Normalises a single-or-multiple value into a list; shared with the flyout levels. */
export const toValueList = <T extends PickerItemValue>(
    value: LooseValue<T>,
): T[] => {
    if (value === undefined || value === null) {
        return [];
    }
    return isValueList(value) ? [...value] : [value];
};

interface UsePickerSelectionOptions<T extends PickerItemValue> {
    isMultiple: boolean;
    value: LooseValue<T>;
    defaultValue: LooseValue<T>;
    onValueChange?: (value: T[] | T | null) => void;
    items?: ReadonlyArray<PickerItemData<T>>;
    renderValue?: (values: readonly T[]) => ReactNode;
    selectionSummary?: PickerSelectionSummary;
    /** Values of exclusive sections to their siblings, dropped when the value is added. */
    exclusiveSiblings?: ReadonlyMap<T, readonly T[]>;
}

/**
 * Normalises a root's single-or-multiple value into one `T[]`, keeps the
 * uncontrolled copy, and remembers what rendered items said about themselves
 * so summaries and section toggles work without the data-driven `items`.
 */
export const usePickerSelection = <T extends PickerItemValue>({
    isMultiple,
    value,
    defaultValue,
    onValueChange,
    items,
    renderValue,
    selectionSummary,
    exclusiveSiblings,
}: UsePickerSelectionOptions<T>) => {
    const isControlled = value !== undefined;
    const [internalValues, setInternalValues] = useState<T[]>(() =>
        toValueList(defaultValue),
    );
    // A controlled `value={[...ids]}` rebuilt on every parent render keeps the
    // previous list while its members are the same.
    const selectedValues = useShallowStable(
        isControlled ? toValueList(value) : internalValues,
    );

    const selectedSet = useMemo(
        () => new Set<PickerItemValue>(selectedValues),
        [selectedValues],
    );
    const isSelected = useCallback(
        (candidate: PickerItemValue) => selectedSet.has(candidate),
        [selectedSet],
    );

    // What composed rows said about themselves. Held as state (copied on a
    // real change) so labels and disabled flags derived from it re-render
    // their consumers through the memoised context.
    const [registry, setRegistry] = useState(
        () => new Map<PickerItemValue, PickerItemRegistration>(),
    );
    const registerItem = useCallback((registration: PickerItemRegistration) => {
        setRegistry((previousRegistry) => {
            const previous = previousRegistry.get(registration.value);
            if (
                previous !== undefined &&
                previous.label === registration.label &&
                previous.isDisabled === registration.isDisabled
            ) {
                return previousRegistry;
            }
            return new Map(previousRegistry).set(
                registration.value,
                registration,
            );
        });
    }, []);

    const itemsByValue = useMemo(
        () =>
            new Map<PickerItemValue, PickerItemData<T>>(
                collectItems(items ?? []).map((item) => [item.value, item]),
            ),
        [items],
    );

    const isOwnValue = useCallback(
        (candidate: PickerItemValue): candidate is T =>
            itemsByValue.has(candidate) || registry.has(candidate),
        [itemsByValue, registry],
    );

    const isValueDisabled = useCallback(
        (candidate: PickerItemValue) =>
            itemsByValue.get(candidate)?.disabled ??
            registry.get(candidate)?.isDisabled ??
            false,
        [itemsByValue, registry],
    );

    const labelFor = useCallback(
        (candidate: T) =>
            itemsByValue.get(candidate)?.label ??
            registry.get(candidate)?.label ??
            String(candidate),
        [itemsByValue, registry],
    );

    const handleValueChange = useLatestCallback(onValueChange);
    const commitValues = useCallback(
        (nextValues: T[]) => {
            const resolvedValues =
                exclusiveSiblings === undefined
                    ? nextValues
                    : keepOnePerExclusiveSection({
                          previousValues: selectedValues,
                          nextValues,
                          siblings: exclusiveSiblings,
                      });
            if (!isControlled) {
                setInternalValues(resolvedValues);
            }
            handleValueChange?.(
                isMultiple ? resolvedValues : (resolvedValues[0] ?? null),
            );
        },
        [
            isControlled,
            isMultiple,
            handleValueChange,
            exclusiveSiblings,
            selectedValues,
        ],
    );

    // Data-driven roots drop values they do not know (a stale chain commit);
    // composed roots take every value, since rows they have not mounted yet
    // (collapsed, filtered out) are still legitimate targets of a section
    // select-all or a controlled value.
    const isDataDriven = items !== undefined;
    const setSelectedValues = useCallback(
        (nextValues: readonly PickerItemValue[]) =>
            commitValues(
                isDataDriven
                    ? nextValues.filter(isOwnValue)
                    : nextValues.filter(
                          (candidate): candidate is T =>
                              typeof candidate === "string" ||
                              typeof candidate === "number",
                      ),
            ),
        [commitValues, isOwnValue, isDataDriven],
    );

    // `renderValue` replaces the label when it returns a string (usable
    // wherever text is required: inputs, aria); any other node is offered
    // separately as `selectionNode`, for the button triggers' value slot.
    let selectionLabel: string | null = null;
    let selectionNode: ReactNode = undefined;
    if (selectedValues.length > 0) {
        const rendered = renderValue?.(selectedValues);
        if (typeof rendered === "string") {
            selectionLabel = rendered;
        } else {
            selectionNode = rendered;
            selectionLabel = isMultiple
                ? summarizeSelection({
                      labels: selectedValues.map(labelFor),
                      summary: selectionSummary,
                  })
                : labelFor(selectedValues[0]);
        }
    }

    return {
        selectedValues,
        isSelected,
        commitValues,
        setSelectedValues,
        registerItem,
        isValueDisabled,
        labelFor,
        itemsByValue,
        selectionLabel,
        selectionNode,
    };
};
